"use client";

import { useScreen } from "@/hooks/use-screen";
import useTypeAnimation from "@/hooks/use-type-animation";
import { cn } from "@/lib/utils";
import { ls } from "@/lib/utils/ls";
import { useEffect } from "react";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";

const welcomeText = `###### **Welcome to GroqChat™, we're glad you're here!**

Please enjoy this fast AI inference demo Powered by the Groq LPU™.

###### **Further resources:**

- Developers, build with fast free API keys for LLMs in the [GroqCloud™ console](https://console.groq.com/).
- Join thousands of Groq Developers on [Discord](https://discord.gg/groq).
- Learn about our open source [model token pricing](https://groq.com/pricing/).
- Follow and get updates on Groq via social media. [LinkedIn](https://www.linkedin.com/company/groq), [X](https://twitter.com/groqinc), and [YouTube](https://www.youtube.com/c/GroqInc).`;

const welcomeTextMobile = `###### **Welcome to GroqChat™, we're glad you're here!**

Please enjoy this fast AI inference demo Powered by the Groq LPU™.`;

type GroqIsFastSectionProps = {
  className?: string;
};

const GroqIsFastSection = ({ className }: GroqIsFastSectionProps) => {
  const { isMobile } = useScreen();
  const paragraphTA = useTypeAnimation({
    string: isMobile ? welcomeTextMobile : welcomeText,
    speed: 1,
    batchSize: 2,
  });

  useEffect(() => {
    const noTypeAnimation = ls.get("noTypeAnimation");
    if (noTypeAnimation) {
      paragraphTA.noAnimation();
      ls.remove("noTypeAnimation");
    }
    paragraphTA.start();
  }, []);

  return (
    <section
      className={cn("w-full px-3 md:p-0 max-w-[900px] pb-5 md:mt-9", className)}
    >
      <ReactMarkdown
        className="text-[0.9rem] leading-6 font-inter mb-2 pb-2"
        remarkPlugins={[remarkGfm]}
        components={{
          h6: ({ children }) => (
            <h6 className="[&:not(:first-child)]:mt-6">{children}</h6>
          ),
          ul: ({ children }) => (
            <ul className="list-square ml-6">{children}</ul>
          ),
          a({ href, children, ...props }) {
            return (
              <a
                className="text-primaryaccent hover:text-[#0AAFC8]"
                href={href}
                {...props}
                target={"_blank"}
                rel="noreferrer"
              >
                {children}
              </a>
            );
          },
        }}
      >
        {paragraphTA.displayResponse}
      </ReactMarkdown>
    </section>
  );
};

export default GroqIsFastSection;
